import {
  AlertCircle,
  AlertTriangle,
  ArrowRight,
  Check,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Info,
  X,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import SpecialistApi from "../../../api/specialistsApi";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import { Textarea } from "../../../components/ui/textarea";
import {
  ProviderCredentialEvidence,
  ProviderEvidenceFile,
  ProviderProfessionalAttributes,
  ProviderWorkExperience,
  SubSpecializations,
} from "../../../types/TypeSpecialist";
import {
  formatProviderCredentialsForReview,
  formatModeratorOnlyProviderEvidenceForReview,
  formatProviderProfessionalAttributesForReview,
  formatProviderWorkExperienceForReview,
} from "../../../utils/providerEvidenceReview";
import {
  FieldChange,
  FieldReviewState,
  VideoLanguageChange,
} from "../../../utils/profileDiffUtils";
import { CertificateComparisonView } from "./CertificateComparisonView";

interface ChangeItemEnhancedProps {
  change: FieldChange;
  userId?: string;
  specialistId?: string; // Required for certificate comparisons (current specialization)
  reviewState?: FieldReviewState;
  mainSpecId: string;
  subSpecializations?: SubSpecializations[];
  onReviewChange?: (state: FieldReviewState) => void;
}

const severityConfig = {
  minor: {
    icon: Info,
    color: "text-blue-400",
    bgColor: "bg-blue-900/30",
    borderColor: "border-blue-700",
    badgeVariant: "secondary" as const,
  },
  major: {
    icon: AlertCircle,
    color: "text-yellow-400",
    bgColor: "bg-yellow-900/30",
    borderColor: "border-yellow-700",
    badgeVariant: "default" as const,
  },
  critical: {
    icon: AlertTriangle,
    color: "text-red-400",
    bgColor: "bg-red-900/30",
    borderColor: "border-red-700",
    badgeVariant: "destructive" as const,
  },
};

const VideoPreview = ({
  videoChange,
  side,
  specialistId,
}: {
  videoChange: VideoLanguageChange;
  side: "old" | "new";
  specialistId?: string;
}) => {
  const directUrl =
    side === "old" ? videoChange.oldVideoUrl : videoChange.newVideoUrl;
  const s3Path =
    side === "old" ? videoChange.oldVideoS3 : videoChange.newVideoS3;
  const [signedUrl, setSignedUrl] = useState<string | undefined>();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    let isActive = true;

    const loadSignedUrl = async () => {
      setSignedUrl(undefined);
      if (!s3Path || !specialistId) return;

      const token = localStorage.getItem("token");
      if (!token) return;

      try {
        setIsLoading(true);
        const response = await SpecialistApi.getIntroVideoS3SignedUrl(
          token,
          specialistId,
          s3Path
        );
        if (isActive && response.status && response.url) {
          setSignedUrl(response.url);
        }
      } catch (error) {
        console.error("Error loading intro video signed URL:", error);
      } finally {
        if (isActive) setIsLoading(false);
      }
    };

    loadSignedUrl();

    return () => {
      isActive = false;
    };
  }, [s3Path, specialistId]);

  const src = directUrl ?? signedUrl;
  const toneClasses =
    side === "old"
      ? "bg-red-900/30 border-red-700 text-red-200"
      : "bg-green-900/30 border-green-700 text-green-200";

  if (src) {
    return (
      <div className={`${toneClasses} border rounded overflow-hidden`}>
        <video
          src={src}
          controls
          crossOrigin="anonymous"
          className="w-full max-h-64 bg-black"
          style={{ maxHeight: "256px" }}
        />
      </div>
    );
  }

  return (
    <div
      className={`${toneClasses} border rounded px-3 py-2 flex items-center justify-center h-32`}
    >
      <p className="text-sm font-medium">
        {isLoading ? "Loading video..." : "No video"}
      </p>
    </div>
  );
};

export const ChangeItemEnhanced = ({
  change,
  userId,
  specialistId,
  reviewState,
  onReviewChange,
  mainSpecId,
  subSpecializations = [],
}: ChangeItemEnhancedProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [openingFilePath, setOpeningFilePath] = useState<string | null>(null);
  const [rejectionReason, setRejectionReason] = useState(
    reviewState?.rejectionReason || ""
  );
  const severity = severityConfig[change.severity];
  const SeverityIcon = severity.icon;
  const containerRef = useRef<HTMLDivElement>(null);

  const handleApprove = () => {
    if (onReviewChange) {
      onReviewChange({ status: "approved" });
    }
  };

  const handleReject = () => {
    setIsRejecting(true);
  };

  const confirmReject = () => {
    if (onReviewChange && rejectionReason.trim()) {
      onReviewChange({
        status: "rejected",
        rejectionReason: rejectionReason.trim(),
      });
      setIsRejecting(false);
    }
  };

  const cancelReject = () => {
    setIsRejecting(false);
    setRejectionReason(reviewState?.rejectionReason || "");
  };

  const getReviewBadge = () => {
    if (!reviewState) return null;

    switch (reviewState.status) {
      case "approved":
        return <Badge className="bg-green-600 text-white">✓ Approved</Badge>;
      case "rejected":
        return <Badge className="bg-red-600 text-white">✗ Rejected</Badge>;
      default:
        return null;
    }
  };

  const getReviewBorderClass = () => {
    if (!reviewState) return "";
    switch (reviewState.status) {
      case "approved":
        return "border-green-500";
      case "rejected":
        return "border-red-500";
      default:
        return "";
    }
  };

  const openProviderEvidenceFile = async (file?: ProviderEvidenceFile) => {
    if (!file) return;

    if (file.url) {
      window.open(file.url, "_blank", "noopener,noreferrer");
      return;
    }

    const token = localStorage.getItem("token");
    if (!token || !specialistId || !file.filepath) return;

    try {
      setOpeningFilePath(file.filepath);
      const response = await SpecialistApi.getProviderEvidenceFileSignedUrl(
        token,
        {
          specialistId,
          filepath: file.filepath,
        }
      );

      if (response.status && response.url) {
        window.open(response.url, "_blank", "noopener,noreferrer");
      }
    } catch (error) {
      console.error("Error opening Provider Evidence file:", error);
    } finally {
      setOpeningFilePath(null);
    }
  };

  const renderWorkExperienceEntries = (
    title: string,
    entries: ProviderWorkExperience[],
    tone: "old" | "new"
  ) => {
    const formattedEntries = formatProviderWorkExperienceForReview(
      entries,
      subSpecializations
    );
    const isOld = tone === "old";
    const containerClasses = isOld
      ? "bg-red-900/30 border-red-700"
      : "bg-green-900/30 border-green-700";
    const textClasses = isOld ? "text-red-100" : "text-green-100";

    return (
      <div className="flex-1 min-w-0">
        <p className="text-xs text-gray-400 mb-2 font-medium">{title}</p>
        <div
          className={`${containerClasses} border rounded px-3 py-3 space-y-3`}
        >
          {formattedEntries.length === 0 ? (
            <p className={`text-sm font-medium ${textClasses}`}>No data</p>
          ) : (
            formattedEntries.map((entry, index) => (
              <div
                key={index}
                className="border border-gray-700/70 rounded bg-gray-950/40 p-3 space-y-3"
              >
                <div className="flex items-start justify-between gap-3">
                  <p className={`text-sm font-semibold ${textClasses}`}>
                    Experience {index + 1}
                  </p>
                  <p className="text-xs text-gray-300 text-right">
                    {entry.startDate} - {entry.endDate}
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs text-gray-400 font-medium mb-1">
                      Company name
                    </p>
                    <div className="space-y-1">
                      {entry.companyName.map((item, itemIndex) => (
                        <p key={itemIndex} className="text-sm text-gray-100">
                          <span className="text-gray-400">
                            {item.language.toUpperCase()}:
                          </span>{" "}
                          {item.text}
                        </p>
                      ))}
                    </div>
                  </div>

                  <div>
                    <p className="text-xs text-gray-400 font-medium mb-1">
                      Job title
                    </p>
                    <div className="space-y-1">
                      {entry.jobTitle.map((item, itemIndex) => (
                        <p key={itemIndex} className="text-sm text-gray-100">
                          <span className="text-gray-400">
                            {item.language.toUpperCase()}:
                          </span>{" "}
                          {item.text}
                        </p>
                      ))}
                    </div>
                  </div>
                </div>

                <div>
                  <p className="text-xs text-gray-400 font-medium mb-1">
                    Main functions
                  </p>
                  <div className="space-y-2">
                    {entry.mainFunctions.map((item, itemIndex) => (
                      <div key={itemIndex}>
                        <p className="text-xs text-gray-400">
                          {item.language.toUpperCase()}
                        </p>
                        <ul className="list-disc pl-5 text-sm text-gray-100">
                          {item.items.map((functionText, functionIndex) => (
                            <li key={functionIndex}>{functionText}</li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <p className="text-xs text-gray-400 font-medium mb-1">
                    Sub-specializations
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {entry.subSpecializations.map((subSpecialization) => (
                      <span
                        key={subSpecialization}
                        className="text-xs text-gray-100 border border-gray-600 rounded px-2 py-1"
                      >
                        {subSpecialization}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    );
  };

  const renderProfessionalAttributes = (
    title: string,
    attributes: ProviderProfessionalAttributes,
    tone: "old" | "new"
  ) => {
    const formattedAttributes = formatProviderProfessionalAttributesForReview(
      attributes,
      subSpecializations
    );
    const isOld = tone === "old";
    const containerClasses = isOld
      ? "bg-red-900/30 border-red-700"
      : "bg-green-900/30 border-green-700";
    const textClasses = isOld ? "text-red-100" : "text-green-100";

    return (
      <div className="flex-1 min-w-0">
        <p className="text-xs text-gray-400 mb-2 font-medium">{title}</p>
        <div
          className={`${containerClasses} border rounded px-3 py-3 space-y-4`}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {formattedAttributes.details.map((detail) => (
              <div
                key={detail.label}
                className="border border-gray-700/70 rounded bg-gray-950/40 p-3"
              >
                <p className="text-xs text-gray-400 font-medium mb-1">
                  {detail.label}
                </p>
                <p className={`text-sm font-medium ${textClasses}`}>
                  {detail.value}
                </p>
              </div>
            ))}
          </div>

          {formattedAttributes.hoursBySubSpecialization.length > 0 && (
            <div>
              <p className="text-xs text-gray-400 font-medium mb-2">
                Hours by sub-specialization
              </p>
              <div className="space-y-2">
                {formattedAttributes.hoursBySubSpecialization.map(
                  (item, index) => (
                    <div
                      key={`${item.subSpecialization}-${index}`}
                      className="flex justify-between gap-3 border border-gray-700/70 rounded bg-gray-950/40 p-2 text-sm"
                    >
                      <span className="text-gray-100">
                        {item.subSpecialization}
                      </span>
                      <span className={textClasses}>{item.hours}</span>
                    </div>
                  )
                )}
              </div>
            </div>
          )}

          {formattedAttributes.casesBySubSpecialization.length > 0 && (
            <div>
              <p className="text-xs text-gray-400 font-medium mb-2">
                Cases by sub-specialization
              </p>
              <div className="space-y-2">
                {formattedAttributes.casesBySubSpecialization.map(
                  (item, index) => (
                    <div
                      key={`${item.subSpecialization}-${index}`}
                      className="flex justify-between gap-3 border border-gray-700/70 rounded bg-gray-950/40 p-2 text-sm"
                    >
                      <span className="text-gray-100">
                        {item.subSpecialization}
                      </span>
                      <span className={textClasses}>{item.cases}</span>
                    </div>
                  )
                )}
              </div>
            </div>
          )}

          {formattedAttributes.methodology.length > 0 && (
            <div>
              <p className="text-xs text-gray-400 font-medium mb-2">
                Methodology
              </p>
              <div className="space-y-2">
                {formattedAttributes.methodology.map((methodology, index) => (
                  <div
                    key={index}
                    className="border border-gray-700/70 rounded bg-gray-950/40 p-3 space-y-2"
                  >
                    <p className="text-xs text-gray-400">
                      Sub-specialization: {methodology.subSpecialization}
                    </p>
                    {methodology.description.map((item, itemIndex) => (
                      <p key={itemIndex} className="text-sm text-gray-100">
                        <span className="text-gray-400">
                          {item.language.toUpperCase()}:
                        </span>{" "}
                        {item.text}
                      </p>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          )}

          {formattedAttributes.industryFocus.length > 0 && (
            <div>
              <p className="text-xs text-gray-400 font-medium mb-2">
                Industry focus
              </p>
              <div className="space-y-2">
                {formattedAttributes.industryFocus.map((item, itemIndex) => (
                  <div key={itemIndex}>
                    <p className="text-xs text-gray-400">
                      {item.language.toUpperCase()}
                    </p>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {item.items.map((industry) => (
                        <span
                          key={industry}
                          className="text-xs text-gray-100 border border-gray-600 rounded px-2 py-1"
                        >
                          {industry}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderProviderCredentials = (
    title: string,
    credentials: ProviderCredentialEvidence[],
    tone: "old" | "new"
  ) => {
    const formattedCredentials = formatProviderCredentialsForReview(credentials);
    const isOld = tone === "old";
    const containerClasses = isOld
      ? "bg-red-900/30 border-red-700"
      : "bg-green-900/30 border-green-700";
    const textClasses = isOld ? "text-red-100" : "text-green-100";

    return (
      <div className="flex-1 min-w-0">
        <p className="text-xs text-gray-400 mb-2 font-medium">{title}</p>
        <div
          className={`${containerClasses} border rounded px-3 py-3 space-y-3`}
        >
          {formattedCredentials.length === 0 ? (
            <p className={`text-sm font-medium ${textClasses}`}>No data</p>
          ) : (
            formattedCredentials.map((credential, index) => (
              <div
                key={`${credential.credentialType}-${index}`}
                className="border border-gray-700/70 rounded bg-gray-950/40 p-3 space-y-3"
              >
                <div className="flex items-start justify-between gap-3">
                  <p className={`text-sm font-semibold ${textClasses}`}>
                    {credential.credentialType}
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {credential.details.map((detail) => (
                    <div key={detail.label}>
                      <p className="text-xs text-gray-400 font-medium mb-1">
                        {detail.label}
                      </p>
                      <p className="text-sm text-gray-100">{detail.value}</p>
                    </div>
                  ))}
                </div>

                {credential.proofFile && (
                  <div className="border border-gray-700/70 rounded bg-gray-900/70 p-3 space-y-2">
                    <div className="flex items-start justify-between gap-3">
                      <p className="text-xs text-gray-400 font-medium">
                        Proof file
                      </p>
                      {credential.proofFile.canOpen &&
                        credential.rawProofFile && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs"
                            disabled={
                              openingFilePath ===
                              credential.rawProofFile.filepath
                            }
                            onClick={(event) => {
                              event.stopPropagation();
                              openProviderEvidenceFile(
                                credential.rawProofFile
                              );
                            }}
                          >
                            <ExternalLink size={13} className="mr-1" />
                            {openingFilePath ===
                            credential.rawProofFile.filepath
                              ? "Opening..."
                              : "Open file"}
                          </Button>
                        )}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-100">
                      <p>
                        <span className="text-gray-400">Name:</span>{" "}
                        {credential.proofFile.originalName}
                      </p>
                      <p>
                        <span className="text-gray-400">Type:</span>{" "}
                        {credential.proofFile.mimeType}
                      </p>
                      <p>
                        <span className="text-gray-400">Size:</span>{" "}
                        {credential.proofFile.size}
                      </p>
                      <p>
                        <span className="text-gray-400">Uploaded:</span>{" "}
                        {credential.proofFile.uploadedAt}
                      </p>
                      <p className="md:col-span-2 break-all">
                        <span className="text-gray-400">Path:</span>{" "}
                        {credential.proofFile.filepath}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    );
  };

  const renderModeratorOnlyProviderEvidence = (
    title: string,
    evidence: Record<string, unknown | ProviderEvidenceFile>,
    tone: "old" | "new"
  ) => {
    const formattedEvidence = formatModeratorOnlyProviderEvidenceForReview(
      evidence
    );
    const isOld = tone === "old";
    const containerClasses = isOld
      ? "bg-red-900/30 border-red-700"
      : "bg-green-900/30 border-green-700";
    const textClasses = isOld ? "text-red-100" : "text-green-100";

    return (
      <div className="flex-1 min-w-0">
        <p className="text-xs text-gray-400 mb-2 font-medium">{title}</p>
        <div
          className={`${containerClasses} border rounded px-3 py-3 space-y-3`}
        >
          {formattedEvidence.length === 0 ? (
            <p className={`text-sm font-medium ${textClasses}`}>No data</p>
          ) : (
            formattedEvidence.map((field) => (
              <div
                key={field.key}
                className="border border-gray-700/70 rounded bg-gray-950/40 p-3 space-y-3"
              >
                <div className="flex items-start justify-between gap-3">
                  <p className={`text-sm font-semibold ${textClasses}`}>
                    {field.label}
                  </p>
                  <Badge variant="outline" className="text-xs">
                    Moderation only
                  </Badge>
                </div>

                {field.values.length > 0 && (
                  <div className="space-y-2">
                    {field.values.map((item, index) => (
                      <div key={index}>
                        {item.label && (
                          <p className="text-xs text-gray-400 font-medium mb-1">
                            {item.label}
                          </p>
                        )}
                        <p className="text-sm text-gray-100 whitespace-pre-wrap break-words">
                          {item.value}
                        </p>
                      </div>
                    ))}
                  </div>
                )}

                {field.file && (
                  <div className="border border-gray-700/70 rounded bg-gray-900/70 p-3 space-y-2">
                    <div className="flex items-start justify-between gap-3">
                      <p className="text-xs text-gray-400 font-medium">
                        Moderator-only file
                      </p>
                      {field.file.canOpen && field.rawFile && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs"
                          disabled={openingFilePath === field.rawFile.filepath}
                          onClick={(event) => {
                            event.stopPropagation();
                            openProviderEvidenceFile(field.rawFile);
                          }}
                        >
                          <ExternalLink size={13} className="mr-1" />
                          {openingFilePath === field.rawFile.filepath
                            ? "Opening..."
                            : "Open file"}
                        </Button>
                      )}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-100">
                      <p>
                        <span className="text-gray-400">Name:</span>{" "}
                        {field.file.originalName}
                      </p>
                      <p>
                        <span className="text-gray-400">Type:</span>{" "}
                        {field.file.mimeType}
                      </p>
                      <p>
                        <span className="text-gray-400">Size:</span>{" "}
                        {field.file.size}
                      </p>
                      <p>
                        <span className="text-gray-400">Uploaded:</span>{" "}
                        {field.file.uploadedAt}
                      </p>
                      <p className="md:col-span-2 break-all">
                        <span className="text-gray-400">Path:</span>{" "}
                        {field.file.filepath}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    );
  };

  // Keyboard shortcuts: Alt+A for Approve, Alt+R for Reject
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only handle if this component is in focus/expanded or if no modal is open
      if (
        !isExpanded &&
        !containerRef.current?.contains(document.activeElement)
      ) {
        return;
      }

      if (e.altKey && !e.ctrlKey && !e.metaKey) {
        if (e.key.toLowerCase() === "a") {
          e.preventDefault();
          if (!reviewState || reviewState.status !== "approved") {
            handleApprove();
          }
        } else if (e.key.toLowerCase() === "r") {
          e.preventDefault();
          if (!reviewState || reviewState.status !== "rejected") {
            handleReject();
          }
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isExpanded, reviewState]);

  // Render review action buttons
  const renderReviewActions = () => {
    if (!onReviewChange) return null;

    if (isRejecting) {
      return (
        <div className="mt-3 p-3 bg-gray-950 rounded border border-gray-700 space-y-2">
          <label className="text-xs text-gray-300 font-medium">
            Rejection Reason:
          </label>
          <Textarea
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            className="min-h-[80px] bg-gray-900 border-gray-600 text-white text-sm"
            placeholder="Explain what needs to be fixed..."
            autoFocus
          />
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="destructive"
              onClick={confirmReject}
              disabled={!rejectionReason.trim()}
              className="flex-1"
            >
              <X size={14} className="mr-1" />
              Confirm Rejection
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={cancelReject}
              className="flex-1"
            >
              Cancel
            </Button>
          </div>
        </div>
      );
    }

    if (reviewState?.status === "rejected") {
      return (
        <div className="mt-3 p-3 bg-red-950/30 rounded border border-red-700 space-y-2">
          <p className="text-xs text-red-300 font-medium">Rejection Reason:</p>
          <p className="text-sm text-red-200">{reviewState.rejectionReason}</p>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setRejectionReason(reviewState.rejectionReason || "");
              setIsRejecting(true);
            }}
            className="text-xs"
          >
            Edit Reason
          </Button>
        </div>
      );
    }

    if (reviewState?.status === "approved") {
      return (
        <div className="mt-3 flex gap-2">
          <Badge className="bg-green-600 text-white">✓ Approved</Badge>
          <Button
            size="sm"
            variant="ghost"
            onClick={handleReject}
            className="text-xs h-6"
          >
            Change to Reject
          </Button>
        </div>
      );
    }

    // Pending state - show approve/reject buttons
    return (
      <div className="mt-3 flex gap-2">
        <Button
          size="sm"
          variant="default"
          onClick={handleApprove}
          className="flex-1 bg-green-600 hover:bg-green-700"
          title="Keyboard shortcut: Alt+A"
        >
          <Check size={14} className="mr-1" />
          Approve <span className="text-xs opacity-70 ml-1">(Alt+A)</span>
        </Button>
        <Button
          size="sm"
          variant="destructive"
          onClick={handleReject}
          className="flex-1"
          title="Keyboard shortcut: Alt+R"
        >
          <X size={14} className="mr-1" />
          Reject <span className="text-xs opacity-70 ml-1">(Alt+R)</span>
        </Button>
      </div>
    );
  };

  // Certificate changes - show expandable comparison with blur feature
  if (change.isCertificate && specialistId) {
    const getCertificateType = (fieldName: string) => {
      if (fieldName === "appreciationCertificates") return "appreciation";
      if (fieldName === "educationCertificates") return "education";
      if (fieldName === "qualificationCertificates") return "qualification";
      if (fieldName === "federationCertificates") return "federation";
      return "appreciation";
    };

    const certificateType = getCertificateType(change.field);

    return (
      <div
        ref={containerRef}
        className={`bg-gray-900 rounded-lg border ${
          severity.borderColor
        } ${getReviewBorderClass()}`}
      >
        <div
          className="flex items-center justify-between p-4 cursor-pointer"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="flex items-center gap-2">
            <SeverityIcon className={severity.color} size={16} />
            <p className="text-sm font-medium text-white">{change.label}</p>
          </div>
          <div className="flex items-center gap-2">
            {getReviewBadge()}
            <Badge variant={severity.badgeVariant} className="text-xs">
              {change.severity}
            </Badge>
            <Badge variant="outline" className="text-xs">
              {change.changeType === "added" && "Added"}
              {change.changeType === "removed" && "Removed"}
              {change.changeType === "modified" && "Modified"}
            </Badge>
            <button className="text-gray-400 hover:text-white transition-colors">
              {isExpanded ? (
                <ChevronDown size={16} />
              ) : (
                <ChevronRight size={16} />
              )}
            </button>
          </div>
        </div>

        {/* Summary view when collapsed */}
        {!isExpanded && (
          <div className="px-4 pb-4">
            <div className="flex items-center gap-4 mb-2">
              <div className="flex-1">
                <p className="text-xs text-gray-400 mb-1 font-medium">
                  Current (Public)
                </p>
                <div className="bg-red-900/30 border border-red-700 rounded px-3 py-2">
                  <p className="text-sm text-red-200 font-medium">
                    {String(change.oldValue) || "-"}
                  </p>
                </div>
              </div>
              <ArrowRight className="text-gray-500 flex-shrink-0" size={20} />
              <div className="flex-1">
                <p className="text-xs text-gray-400 mb-1 font-medium">
                  Updated (Pending)
                </p>
                <div className="bg-green-900/30 border border-green-700 rounded px-3 py-2">
                  <p className="text-sm text-green-200 font-medium">
                    {String(change.newValue) || "-"}
                  </p>
                </div>
              </div>
            </div>
            {renderReviewActions()}
          </div>
        )}

        {/* Expanded certificate comparison with blur feature */}
        {isExpanded && (
          <>
            <div className="border-t border-gray-700">
              <CertificateComparisonView
                certificateType={certificateType as any}
                specialistId={specialistId}
                mainSpecId={mainSpecId}
                label={change.label}
              />
            </div>
            <div className="px-4 pb-4">{renderReviewActions()}</div>
          </>
        )}
      </div>
    );
  }

  if (change.isProviderWorkExperience) {
    const oldEntries = Array.isArray(change.oldValue) ? change.oldValue : [];
    const newEntries = Array.isArray(change.newValue) ? change.newValue : [];

    return (
      <div
        ref={containerRef}
        className={`bg-gray-900 rounded-lg border ${
          severity.borderColor
        } ${getReviewBorderClass()}`}
      >
        <div
          className="flex items-center justify-between p-4 cursor-pointer"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="flex items-center gap-2">
            <SeverityIcon className={severity.color} size={16} />
            <div>
              <p className="text-sm font-medium text-white">{change.label}</p>
              {change.summary && (
                <p className="text-xs text-gray-400 mt-1">{change.summary}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {getReviewBadge()}
            <Badge variant={severity.badgeVariant} className="text-xs">
              {change.severity}
            </Badge>
            <Badge variant="outline" className="text-xs">
              {change.changeType === "added" && "Added"}
              {change.changeType === "removed" && "Removed"}
              {change.changeType === "modified" && "Modified"}
            </Badge>
            <button className="text-gray-400 hover:text-white transition-colors">
              {isExpanded ? (
                <ChevronDown size={16} />
              ) : (
                <ChevronRight size={16} />
              )}
            </button>
          </div>
        </div>

        {!isExpanded && (
          <div className="px-4 pb-4">
            <div className="flex items-center gap-4 mb-2">
              <div className="flex-1">
                <p className="text-xs text-gray-400 mb-1 font-medium">
                  Current (Public)
                </p>
                <div className="bg-red-900/30 border border-red-700 rounded px-3 py-2">
                  <p className="text-sm text-red-200 font-medium">
                    {oldEntries.length} experience item(s)
                  </p>
                </div>
              </div>
              <ArrowRight className="text-gray-500 flex-shrink-0" size={20} />
              <div className="flex-1">
                <p className="text-xs text-gray-400 mb-1 font-medium">
                  Updated (Pending)
                </p>
                <div className="bg-green-900/30 border border-green-700 rounded px-3 py-2">
                  <p className="text-sm text-green-200 font-medium">
                    {newEntries.length} experience item(s)
                  </p>
                </div>
              </div>
            </div>
            {renderReviewActions()}
          </div>
        )}

        {isExpanded && (
          <>
            <div className="border-t border-gray-700 p-4">
              <div className="flex items-start gap-4">
                {renderWorkExperienceEntries(
                  "Current (Public)",
                  oldEntries,
                  "old"
                )}
                <ArrowRight
                  className="text-gray-500 flex-shrink-0 mt-12"
                  size={20}
                />
                {renderWorkExperienceEntries(
                  "Updated (Pending)",
                  newEntries,
                  "new"
                )}
              </div>
            </div>
            <div className="px-4 pb-4">{renderReviewActions()}</div>
          </>
        )}
      </div>
    );
  }

  if (change.isProviderProfessionalAttributes) {
    const oldAttributes =
      change.oldValue && typeof change.oldValue === "object"
        ? change.oldValue
        : {};
    const newAttributes =
      change.newValue && typeof change.newValue === "object"
        ? change.newValue
        : {};

    return (
      <div
        ref={containerRef}
        className={`bg-gray-900 rounded-lg border ${
          severity.borderColor
        } ${getReviewBorderClass()}`}
      >
        <div
          className="flex items-center justify-between p-4 cursor-pointer"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="flex items-center gap-2">
            <SeverityIcon className={severity.color} size={16} />
            <div>
              <p className="text-sm font-medium text-white">{change.label}</p>
              {change.summary && (
                <p className="text-xs text-gray-400 mt-1">{change.summary}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {getReviewBadge()}
            <Badge variant={severity.badgeVariant} className="text-xs">
              {change.severity}
            </Badge>
            <Badge variant="outline" className="text-xs">
              {change.changeType === "added" && "Added"}
              {change.changeType === "removed" && "Removed"}
              {change.changeType === "modified" && "Modified"}
            </Badge>
            <button className="text-gray-400 hover:text-white transition-colors">
              {isExpanded ? (
                <ChevronDown size={16} />
              ) : (
                <ChevronRight size={16} />
              )}
            </button>
          </div>
        </div>

        {!isExpanded && (
          <div className="px-4 pb-4">
            <div className="flex items-center gap-4 mb-2">
              <div className="flex-1">
                <p className="text-xs text-gray-400 mb-1 font-medium">
                  Current (Public)
                </p>
                <div className="bg-red-900/30 border border-red-700 rounded px-3 py-2">
                  <p className="text-sm text-red-200 font-medium">
                    {Object.keys(oldAttributes).length} attribute field(s)
                  </p>
                </div>
              </div>
              <ArrowRight className="text-gray-500 flex-shrink-0" size={20} />
              <div className="flex-1">
                <p className="text-xs text-gray-400 mb-1 font-medium">
                  Updated (Pending)
                </p>
                <div className="bg-green-900/30 border border-green-700 rounded px-3 py-2">
                  <p className="text-sm text-green-200 font-medium">
                    {Object.keys(newAttributes).length} attribute field(s)
                  </p>
                </div>
              </div>
            </div>
            {renderReviewActions()}
          </div>
        )}

        {isExpanded && (
          <>
            <div className="border-t border-gray-700 p-4">
              <div className="flex items-start gap-4">
                {renderProfessionalAttributes(
                  "Current (Public)",
                  oldAttributes,
                  "old"
                )}
                <ArrowRight
                  className="text-gray-500 flex-shrink-0 mt-12"
                  size={20}
                />
                {renderProfessionalAttributes(
                  "Updated (Pending)",
                  newAttributes,
                  "new"
                )}
              </div>
            </div>
            <div className="px-4 pb-4">{renderReviewActions()}</div>
          </>
        )}
      </div>
    );
  }

  if (change.isProviderCredentials) {
    const oldCredentials = Array.isArray(change.oldValue)
      ? change.oldValue
      : [];
    const newCredentials = Array.isArray(change.newValue)
      ? change.newValue
      : [];

    return (
      <div
        ref={containerRef}
        className={`bg-gray-900 rounded-lg border ${
          severity.borderColor
        } ${getReviewBorderClass()}`}
      >
        <div
          className="flex items-center justify-between p-4 cursor-pointer"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="flex items-center gap-2">
            <SeverityIcon className={severity.color} size={16} />
            <div>
              <p className="text-sm font-medium text-white">{change.label}</p>
              {change.summary && (
                <p className="text-xs text-gray-400 mt-1">{change.summary}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {getReviewBadge()}
            <Badge variant={severity.badgeVariant} className="text-xs">
              {change.severity}
            </Badge>
            <Badge variant="outline" className="text-xs">
              {change.changeType === "added" && "Added"}
              {change.changeType === "removed" && "Removed"}
              {change.changeType === "modified" && "Modified"}
            </Badge>
            <button className="text-gray-400 hover:text-white transition-colors">
              {isExpanded ? (
                <ChevronDown size={16} />
              ) : (
                <ChevronRight size={16} />
              )}
            </button>
          </div>
        </div>

        {!isExpanded && (
          <div className="px-4 pb-4">
            <div className="flex items-center gap-4 mb-2">
              <div className="flex-1">
                <p className="text-xs text-gray-400 mb-1 font-medium">
                  Current (Public)
                </p>
                <div className="bg-red-900/30 border border-red-700 rounded px-3 py-2">
                  <p className="text-sm text-red-200 font-medium">
                    {oldCredentials.length} credential(s)
                  </p>
                </div>
              </div>
              <ArrowRight className="text-gray-500 flex-shrink-0" size={20} />
              <div className="flex-1">
                <p className="text-xs text-gray-400 mb-1 font-medium">
                  Updated (Pending)
                </p>
                <div className="bg-green-900/30 border border-green-700 rounded px-3 py-2">
                  <p className="text-sm text-green-200 font-medium">
                    {newCredentials.length} credential(s)
                  </p>
                </div>
              </div>
            </div>
            {renderReviewActions()}
          </div>
        )}

        {isExpanded && (
          <>
            <div className="border-t border-gray-700 p-4">
              <div className="flex items-start gap-4">
                {renderProviderCredentials(
                  "Current (Public)",
                  oldCredentials,
                  "old"
                )}
                <ArrowRight
                  className="text-gray-500 flex-shrink-0 mt-12"
                  size={20}
                />
                {renderProviderCredentials(
                  "Updated (Pending)",
                  newCredentials,
                  "new"
                )}
              </div>
            </div>
            <div className="px-4 pb-4">{renderReviewActions()}</div>
          </>
        )}
      </div>
    );
  }

  if (change.isModeratorOnlyProviderEvidence) {
    const oldEvidence =
      change.oldValue && typeof change.oldValue === "object"
        ? change.oldValue
        : {};
    const newEvidence =
      change.newValue && typeof change.newValue === "object"
        ? change.newValue
        : {};

    return (
      <div
        ref={containerRef}
        className={`bg-gray-900 rounded-lg border ${
          severity.borderColor
        } ${getReviewBorderClass()}`}
      >
        <div
          className="flex items-center justify-between p-4 cursor-pointer"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="flex items-center gap-2">
            <SeverityIcon className={severity.color} size={16} />
            <div>
              <div className="flex items-center gap-2">
                <p className="text-sm font-medium text-white">
                  {change.label}
                </p>
                <Badge variant="outline" className="text-xs">
                  Not public
                </Badge>
              </div>
              {change.summary && (
                <p className="text-xs text-gray-400 mt-1">{change.summary}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {getReviewBadge()}
            <Badge variant={severity.badgeVariant} className="text-xs">
              {change.severity}
            </Badge>
            <Badge variant="outline" className="text-xs">
              {change.changeType === "added" && "Added"}
              {change.changeType === "removed" && "Removed"}
              {change.changeType === "modified" && "Modified"}
            </Badge>
            <button className="text-gray-400 hover:text-white transition-colors">
              {isExpanded ? (
                <ChevronDown size={16} />
              ) : (
                <ChevronRight size={16} />
              )}
            </button>
          </div>
        </div>

        <div className="px-4 pb-2">
          <div className="bg-yellow-900/30 border border-yellow-700 rounded px-3 py-2">
            <p className="text-xs text-yellow-100">
              Submitted for moderation review only. This evidence is not public
              Specialist profile data.
            </p>
          </div>
        </div>

        {!isExpanded && (
          <div className="px-4 pb-4">
            <div className="flex items-center gap-4 mb-2">
              <div className="flex-1">
                <p className="text-xs text-gray-400 mb-1 font-medium">
                  Current (Public)
                </p>
                <div className="bg-red-900/30 border border-red-700 rounded px-3 py-2">
                  <p className="text-sm text-red-200 font-medium">
                    {Object.keys(oldEvidence).length} moderation-only field(s)
                  </p>
                </div>
              </div>
              <ArrowRight className="text-gray-500 flex-shrink-0" size={20} />
              <div className="flex-1">
                <p className="text-xs text-gray-400 mb-1 font-medium">
                  Updated (Pending)
                </p>
                <div className="bg-green-900/30 border border-green-700 rounded px-3 py-2">
                  <p className="text-sm text-green-200 font-medium">
                    {Object.keys(newEvidence).length} moderation-only field(s)
                  </p>
                </div>
              </div>
            </div>
            {renderReviewActions()}
          </div>
        )}

        {isExpanded && (
          <>
            <div className="border-t border-gray-700 p-4">
              <div className="flex items-start gap-4">
                {renderModeratorOnlyProviderEvidence(
                  "Current (Public)",
                  oldEvidence,
                  "old"
                )}
                <ArrowRight
                  className="text-gray-500 flex-shrink-0 mt-12"
                  size={20}
                />
                {renderModeratorOnlyProviderEvidence(
                  "Updated (Pending)",
                  newEvidence,
                  "new"
                )}
              </div>
            </div>
            <div className="px-4 pb-4">{renderReviewActions()}</div>
          </>
        )}
      </div>
    );
  }

  // Multi-language video changes
  if (change.isVideo && change.videoChanges) {
    const hasMultipleLangs = change.videoChanges.length > 1;
    const primaryChange = change.videoChanges[0];

    return (
      <div
        ref={containerRef}
        className={`bg-gray-900 rounded-lg p-4 border ${
          severity.borderColor
        } ${getReviewBorderClass()}`}
      >
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <SeverityIcon className={severity.color} size={16} />
            <p className="text-sm font-medium text-white">{change.label}</p>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant={severity.badgeVariant} className="text-xs">
              {change.severity}
            </Badge>
            {hasMultipleLangs && (
              <>
                <Badge variant="outline" className="text-xs">
                  {change.videoChanges.length} languages
                </Badge>
                <button
                  onClick={() => setIsExpanded(!isExpanded)}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  {isExpanded ? (
                    <ChevronDown size={16} />
                  ) : (
                    <ChevronRight size={16} />
                  )}
                </button>
              </>
            )}
          </div>
        </div>

        {/* Primary video comparison */}
        <div className="flex items-start gap-4">
          <div className="flex-1">
            <p className="text-xs text-gray-400 mb-2 font-medium">
              Current (Public){" "}
              {hasMultipleLangs && `- ${primaryChange.language.toUpperCase()}`}
            </p>
            <VideoPreview
              videoChange={primaryChange}
              side="old"
              specialistId={specialistId}
            />
          </div>
          <ArrowRight className="text-gray-500 flex-shrink-0 mt-12" size={20} />
          <div className="flex-1">
            <p className="text-xs text-gray-400 mb-2 font-medium">
              Updated (Pending){" "}
              {hasMultipleLangs && `- ${primaryChange.language.toUpperCase()}`}
            </p>
            <VideoPreview
              videoChange={primaryChange}
              side="new"
              specialistId={specialistId}
            />
          </div>
        </div>

        {/* Expanded video languages */}
        {isExpanded && hasMultipleLangs && (
          <div className="mt-4 pt-4 border-t border-gray-700 space-y-4">
            {change.videoChanges.slice(1).map((videoChange, idx) => (
              <div key={idx} className="flex items-start gap-4">
                <div className="flex-1">
                  <p className="text-xs text-gray-400 mb-2 font-medium">
                    {videoChange.language.toUpperCase()} - Current
                  </p>
                  <VideoPreview
                    videoChange={videoChange}
                    side="old"
                    specialistId={specialistId}
                  />
                </div>
                <ArrowRight
                  className="text-gray-500 flex-shrink-0 mt-12"
                  size={20}
                />
                <div className="flex-1">
                  <p className="text-xs text-gray-400 mb-2 font-medium">
                    {videoChange.language.toUpperCase()} - Updated
                  </p>
                  <VideoPreview
                    videoChange={videoChange}
                    side="new"
                    specialistId={specialistId}
                  />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Review Actions */}
        {renderReviewActions()}
      </div>
    );
  }

  // Multi-language text changes
  if (change.isMultiLang && change.languageChanges) {
    const hasMultipleLangs = change.languageChanges.length > 1;
    const primaryChange = change.languageChanges[0];

    return (
      <div
        ref={containerRef}
        className={`bg-gray-900 rounded-lg p-4 border ${
          severity.borderColor
        } ${getReviewBorderClass()}`}
      >
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <SeverityIcon className={severity.color} size={16} />
            <p className="text-sm font-medium text-white">{change.label}</p>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant={severity.badgeVariant} className="text-xs">
              {change.severity}
            </Badge>
            {hasMultipleLangs && (
              <>
                <Badge variant="outline" className="text-xs">
                  {change.languageChanges.length} languages
                </Badge>
                <button
                  onClick={() => setIsExpanded(!isExpanded)}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  {isExpanded ? (
                    <ChevronDown size={16} />
                  ) : (
                    <ChevronRight size={16} />
                  )}
                </button>
              </>
            )}
          </div>
        </div>

        {/* Primary language comparison */}
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <p className="text-xs text-gray-400 mb-1 font-medium">
              Current (Public){" "}
              {hasMultipleLangs && `- ${primaryChange.language.toUpperCase()}`}
            </p>
            <div className="bg-red-900/30 border border-red-700 rounded px-3 py-2 max-h-32 overflow-y-auto">
              <p className="text-sm text-red-200 font-medium whitespace-pre-wrap">
                {primaryChange.oldValue || "-"}
              </p>
            </div>
          </div>
          <ArrowRight className="text-gray-500 flex-shrink-0" size={20} />
          <div className="flex-1">
            <p className="text-xs text-gray-400 mb-1 font-medium">
              Updated (Pending){" "}
              {hasMultipleLangs && `- ${primaryChange.language.toUpperCase()}`}
            </p>
            <div className="bg-green-900/30 border border-green-700 rounded px-3 py-2 max-h-32 overflow-y-auto">
              <p className="text-sm text-green-200 font-medium whitespace-pre-wrap">
                {primaryChange.newValue || "-"}
              </p>
            </div>
          </div>
        </div>

        {/* Expanded language changes */}
        {isExpanded && hasMultipleLangs && (
          <div className="mt-4 pt-4 border-t border-gray-700 space-y-3">
            {change.languageChanges.slice(1).map((langChange, idx) => (
              <div key={idx} className="flex items-center gap-4">
                <div className="flex-1">
                  <p className="text-xs text-gray-400 mb-1 font-medium">
                    {langChange.language.toUpperCase()} - Current
                  </p>
                  <div className="bg-red-900/30 border border-red-700 rounded px-3 py-2 max-h-32 overflow-y-auto">
                    <p className="text-sm text-red-200 font-medium whitespace-pre-wrap">
                      {langChange.oldValue || "-"}
                    </p>
                  </div>
                </div>
                <ArrowRight className="text-gray-500 flex-shrink-0" size={20} />
                <div className="flex-1">
                  <p className="text-xs text-gray-400 mb-1 font-medium">
                    {langChange.language.toUpperCase()} - Updated
                  </p>
                  <div className="bg-green-900/30 border border-green-700 rounded px-3 py-2 max-h-32 overflow-y-auto">
                    <p className="text-sm text-green-200 font-medium whitespace-pre-wrap">
                      {langChange.newValue || "-"}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Review Actions */}
        {renderReviewActions()}
      </div>
    );
  }

  // Standard field changes
  return (
    <div
      ref={containerRef}
      className={`bg-gray-900 rounded-lg p-4 border ${
        severity.borderColor
      } ${getReviewBorderClass()}`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <SeverityIcon className={severity.color} size={16} />
          <p className="text-sm font-medium text-white">{change.label}</p>
        </div>
        <div className="flex items-center gap-2">
          {getReviewBadge()}
          <Badge variant={severity.badgeVariant} className="text-xs">
            {change.severity}
          </Badge>
        </div>
      </div>
      <div className="flex items-center gap-4">
        <div className="flex-1">
          <p className="text-xs text-gray-400 mb-1 font-medium">
            Current (Public)
          </p>
          <div className="bg-red-900/30 border border-red-700 rounded px-3 py-2">
            <p className="text-sm text-red-200 font-medium">
              {String(change.oldValue) || "-"}
            </p>
          </div>
        </div>
        <ArrowRight className="text-gray-500 flex-shrink-0" size={20} />
        <div className="flex-1">
          <p className="text-xs text-gray-400 mb-1 font-medium">
            Updated (Pending)
          </p>
          <div className="bg-green-900/30 border border-green-700 rounded px-3 py-2">
            <p className="text-sm text-green-200 font-medium">
              {String(change.newValue) || "-"}
            </p>
          </div>
        </div>
      </div>

      {/* Review Actions */}
      {renderReviewActions()}
    </div>
  );
};
